// Browser half of the first-party analytics. Posts to /api/track via sendBeacon (survives page
// unload, never blocks navigation) with a fetch(keepalive) fallback. Everything is best-effort
// and a no-op on the server.
//
// Identity: `smt_vid` first-party cookie (random uuid, 1 year) + `smt_sid` in sessionStorage
// that rolls over after 30 minutes of inactivity. Nothing personal is stored client-side.
//
// Prefer `track()` from '@/lib/analytics' for product events — it mirrors to GA4 AND here.
// Call `trackEvent()` directly only for events that must NOT go to GA.

const VID_COOKIE = 'smt_vid';
const SID_KEY = 'smt_sid';
const SID_TS_KEY = 'smt_sid_ts';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const ENDPOINT = '/api/track';

export type TrackPayload = {
  type: 'pageview' | 'event' | 'ad_impression';
  name?: string;
  path?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  visitorId?: string;
  sessionId?: string;
  props?: Record<string, unknown>;
};

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through */ }
  // Older WebViews: 32 hex chars from getRandomValues / Math.random.
  let out = '';
  try {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    buf.forEach((b) => { out += b.toString(16).padStart(2, '0'); });
    return out;
  } catch {
    for (let i = 0; i < 32; i++) out += Math.floor(Math.random() * 16).toString(16);
    return out;
  }
}

function readCookie(name: string): string | null {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

/** Stable anonymous visitor id (first-party cookie, 1 year, SameSite=Lax). */
export function getVisitorId(): string | undefined {
  if (!isBrowser()) return undefined;
  const existing = readCookie(VID_COOKIE);
  if (existing && /^[A-Za-z0-9-]{8,64}$/.test(existing)) return existing;
  const id = randomId();
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${VID_COOKIE}=${id}; Max-Age=${365 * 24 * 60 * 60}; Path=/; SameSite=Lax${secure}`;
  } catch { /* cookies disabled — id lives for this page only */ }
  return id;
}

/** Session id with a 30-minute inactivity rollover; touching it extends the session. */
export function getSessionId(): string | undefined {
  if (!isBrowser()) return undefined;
  try {
    const now = Date.now();
    const sid = sessionStorage.getItem(SID_KEY);
    const ts = Number(sessionStorage.getItem(SID_TS_KEY) || 0);
    if (sid && ts && now - ts < SESSION_TIMEOUT_MS) {
      sessionStorage.setItem(SID_TS_KEY, String(now));
      return sid;
    }
    const fresh = randomId();
    sessionStorage.setItem(SID_KEY, fresh);
    sessionStorage.setItem(SID_TS_KEY, String(now));
    return fresh;
  } catch {
    return undefined;
  }
}

/** Low-level send. Adds visitor/session ids, then beacons (text/plain body — see /api/track). */
export function sendTrack(payload: TrackPayload): void {
  if (!isBrowser()) return;
  try {
    const body = JSON.stringify({
      ...payload,
      visitorId: payload.visitorId ?? getVisitorId(),
      sessionId: payload.sessionId ?? getSessionId(),
    });
    if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(ENDPOINT, body)) return;
    fetch(ENDPOINT, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
  } catch { /* analytics is best-effort */ }
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

/** Page view for the current location (called by VisitorTracker on every route change). */
export function trackPageview(pathname?: string, search?: string): void {
  if (!isBrowser()) return;
  const path = pathname || location.pathname;
  if (path.startsWith('/admin')) return;
  const payload: TrackPayload = { type: 'pageview', path, referrer: document.referrer || undefined };
  try {
    const params = new URLSearchParams(search ?? location.search);
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) payload[k] = v.slice(0, 200);
    }
  } catch { /* ignore */ }
  sendTrack(payload);
}

/** Custom event. Props are allow-listed server-side; keep them flat and small. */
export function trackEvent(name: string, props?: Record<string, unknown>): void {
  if (!isBrowser() || !name) return;
  const path = location.pathname;
  if (path.startsWith('/admin')) return;
  sendTrack({ type: 'event', name: name.slice(0, 100), path, props });
}
